<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { saveAs } from 'file-saver';
import { PrimaryButton, useSnackbarStore } from '@es-bill/es-bill-common-components';
import BillrunsDetailsResultsTable from '@/components/BillrunsDetailsResultsTable.vue';
import BillrunsEnergyDataRequestDialog from '@/components/BillrunsEnergyDataRequestDialog.vue';
import {
  exportDetailRowsToCsvBlob,
  getMaxDateRangeCount,
  prepareFilteredDetailRows,
} from '@/services/billrunEnergyDataExportsService';
import {
  buildDetailRowFilterOptions,
} from '@/services/billrunFiltersService';
import {
  buildEnergyDataRequests,
  submitEnergyDataRequests,
} from '@/services/billrunEnergyDataRequestsService';
import { useBillrunsDetailsStore } from '@/stores/billrunsDetailsStore';
import { useCommunitiesStore } from '@/stores/communitiesStore';
import { useMeteringPointMembershipsStore } from '@/stores/meteringPointMembershipsStore';

const detailsStore = useBillrunsDetailsStore();
const communitiesStore = useCommunitiesStore();
const mpmStore = useMeteringPointMembershipsStore();
const snackbarStore = useSnackbarStore();
const { t } = useI18n();

const selectedDsoEcns = ref([]);
const selectedCommunityEcids = ref([]);

const energyRequestPreview = ref({
  requests: [],
  unresolvedMeterPoints: [],
  summary: null,
});
const energyRequestPreviewLoading = ref(false);
const energyRequestDialogOpen = ref(false);
const submitting = ref(false);
const submissionProgress = ref({ current: 0, total: 0 });

let previewDebounceTimer = null;
let lastProgressUpdate = 0;

const isDataFullyLoaded = computed(
  () =>
    detailsStore.hasFetched
    && !detailsStore.loading
    && !detailsStore.analysisLoading
);

const communitiesByEcid = computed(() => {
  const map = new Map();
  for (const c of communitiesStore.allCommunities) {
    if (c.ecid) {
      map.set(c.ecid, c);
    }
  }
  return map;
});

const filterOptions = computed(() =>
  buildDetailRowFilterOptions(detailsStore.detailRows)
);

const dsoFilterOptions = computed(() => filterOptions.value.dsoOptions);

const communityFilterOptions = computed(() => filterOptions.value.communityOptions);

const filteredDetailRows = computed(() =>
  prepareFilteredDetailRows(
    detailsStore.detailRows,
    selectedDsoEcns.value,
    selectedCommunityEcids.value
  )
);

const energyRequestCount = computed(
  () => energyRequestPreview.value.summary?.totals?.requestCount ?? 0
);

const maxDateRangeCount = computed(() =>
  getMaxDateRangeCount(filteredDetailRows.value)
);

const billingRelevantFilterMode = computed({
  get() {
    return detailsStore.billingRelevantMeterCodesOnly ? 'billingRelevant' : 'all';
  },
  set(value) {
    detailsStore.setBillingRelevantMeterCodesOnly(value === 'billingRelevant');
  },
});

async function refreshEnergyRequestPreview() {
  if (!isDataFullyLoaded.value) {
    energyRequestPreview.value = {
      requests: [],
      unresolvedMeterPoints: [],
      summary: null,
    };
    return;
  }

  energyRequestPreviewLoading.value = true;
  try {
    if (communitiesStore.allCommunities.length === 0) {
      await communitiesStore.fetchCommunities();
    }

    const result = await buildEnergyDataRequests({
      billruns: detailsStore.openBillruns,
      analysisByBillRunKey: detailsStore.analysisByBillRunKey,
      interpretationByBillRunKey: detailsStore.interpretationByBillRunKey,
      billingRelevantOnly: detailsStore.billingRelevantMeterCodesOnly,
      selectedDsoEcns: selectedDsoEcns.value,
      selectedCommunityEcids: selectedCommunityEcids.value,
      communitiesByEcid: communitiesByEcid.value,
      memberships: mpmStore.allMeteringPointMemberships,
    });
    energyRequestPreview.value = result;
  } catch {
    energyRequestPreview.value = {
      requests: [],
      unresolvedMeterPoints: [],
      summary: null,
    };
  } finally {
    energyRequestPreviewLoading.value = false;
  }
}

watch(
  () => detailsStore.detailRows,
  () => {
    selectedDsoEcns.value = [];
    selectedCommunityEcids.value = [];
  }
);

watch(dsoFilterOptions, (options) => {
  const available = new Set(options.map((o) => o.value));
  selectedDsoEcns.value = selectedDsoEcns.value.filter((v) => available.has(v));
});

watch(communityFilterOptions, (options) => {
  const available = new Set(options.map((o) => o.value));
  selectedCommunityEcids.value = selectedCommunityEcids.value.filter((v) => available.has(v));
});

watch(
  [
    isDataFullyLoaded,
    selectedDsoEcns,
    selectedCommunityEcids,
    billingRelevantFilterMode,
    () => detailsStore.openBillruns,
    () => detailsStore.analysisByBillRunKey,
    () => detailsStore.interpretationByBillRunKey,
  ],
  () => {
    scheduleEnergyRequestPreviewRefresh();
  },
  { deep: true }
);

function scheduleEnergyRequestPreviewRefresh() {
  if (previewDebounceTimer) {
    clearTimeout(previewDebounceTimer);
  }
  previewDebounceTimer = setTimeout(() => {
    refreshEnergyRequestPreview();
  }, 300);
}

function openEnergyRequestDialog() {
  if (energyRequestCount.value === 0) {
    return;
  }
  energyRequestDialogOpen.value = true;
}

function throttledSubmissionProgress({ completed, total }) {
  const now = Date.now();
  if (now - lastProgressUpdate >= 200 || completed === total) {
    lastProgressUpdate = now;
    submissionProgress.value = { current: completed, total };
  }
}

function showSubmitSnackbar(stats) {
  const message = t('billrunsDetails.requestEnergyDataSubmitMessage', {
    succeeded: stats.callsSucceeded,
    failed: stats.callsFailed,
    total: stats.callsGenerated,
  });
  if (stats.callsFailed === 0) {
    snackbarStore.showSnackbar(
      t('billrunsDetails.requestEnergyDataSubmitSuccess'),
      message,
      'Success',
      5000,
      []
    );
  } else {
    snackbarStore.showSnackbar(
      t('billrunsDetails.requestEnergyDataSubmitPartial'),
      message,
      'Warning',
      8000,
      []
    );
  }
}

async function confirmEnergyDataRequests() {
  const requests = energyRequestPreview.value.requests;
  if (requests.length === 0 || submitting.value) {
    return;
  }

  submitting.value = true;
  lastProgressUpdate = 0;
  submissionProgress.value = { current: 0, total: requests.length };

  try {
    const stats = await submitEnergyDataRequests(requests, {
      onProgress: throttledSubmissionProgress,
    });
    submissionProgress.value = {
      current: requests.length,
      total: requests.length,
    };
    energyRequestDialogOpen.value = false;
    showSubmitSnackbar(stats);
  } finally {
    submitting.value = false;
  }
}

async function onGetData() {
  try {
    await detailsStore.fetchAllDetailsData();
  } catch {
    // error stored in detailsStore.error
  }
}

function exportCsv() {
  if (filteredDetailRows.value.length === 0) {
    return;
  }
  const { blob, fileName } = exportDetailRowsToCsvBlob(filteredDetailRows.value);
  saveAs(blob, fileName);
}
</script>

<template>
  <v-container class="containerBase pa-0">
    <v-container class="containerStandard">
      <h1 class="text-h4 mb-6">{{ $t('billrunsDetails.title') }}</h1>

      <v-card class="mb-4" elevation="2">
        <v-card-title>
          <v-icon class="mr-2" color="primary">mdi-cog</v-icon>
          {{ $t('setup') }}
        </v-card-title>
        <v-card-text>
          <div class="d-flex flex-wrap align-center ga-4">
            <v-text-field
              v-model.number="detailsStore.offsetDays"
              :label="$t('billrunsDashboard.offsetDays')"
              type="number"
              min="0"
              variant="outlined"
              bg-color="white"
              class="offset-days-field"
              hide-details
            />
            <div class="d-flex align-center ga-2">
              <span class="text-body-2 text-medium-emphasis">
                {{ $t('billrunsDashboard.billingRelevantMeterCodes') }}
                <v-tooltip activator="parent" location="top">
                  <span style="white-space: pre-line">
                    {{ $t('billrunsDashboard.billingRelevantMeterCodesTooltip') }}
                  </span>
                </v-tooltip>
              </span>
              <v-btn-toggle
                v-model="billingRelevantFilterMode"
                mandatory
                divided
                density="compact"
                color="primary"
              >
                <v-btn value="billingRelevant" size="small">
                  {{ $t('billrunsDashboard.billingRelevantFilterRelevant') }}
                </v-btn>
                <v-btn value="all" size="small">{{ $t('all') }}</v-btn>
              </v-btn-toggle>
            </div>
          </div>
          <v-row>
            <v-col cols="12" sm="4" md="3">
              <PrimaryButton
                :text="$t('billrunsDashboard.getData')"
                :loading="detailsStore.loading"
                @click="onGetData"
              />
            </v-col>
          </v-row>
          <v-row v-if="isDataFullyLoaded" align="center" class="mt-2 ga-4">
            <v-col cols="12" sm="auto" class="d-flex align-center flex-wrap ga-2">
              <v-autocomplete
                v-model="selectedDsoEcns"
                :items="dsoFilterOptions"
                :label="$t('billrunsDetails.columnDso')"
                item-title="title"
                item-value="value"
                multiple
                chips
                closable-chips
                clearable
                variant="outlined"
                density="compact"
                hide-details
                bg-color="white"
                class="filter-field"
              />
            </v-col>
            <v-col cols="12" sm="auto" class="d-flex align-center flex-wrap ga-2">
              <v-autocomplete
                v-model="selectedCommunityEcids"
                :items="communityFilterOptions"
                :label="$t('billrunsDashboard.communities')"
                :filter-keys="['title', 'value', 'ecn']"
                item-title="title"
                item-value="value"
                multiple
                chips
                closable-chips
                clearable
                variant="outlined"
                density="compact"
                hide-details
                bg-color="white"
                class="filter-field"
              />
            </v-col>
            <v-col cols="12" sm="auto" class="d-flex flex-wrap ga-2">
              <PrimaryButton
                :text="$t('billrunsDetails.exportCsv')"
                :disabled="filteredDetailRows.length === 0"
                @click="exportCsv"
              />
              <PrimaryButton
                :text="$t('billrunsDetails.requestEnergyData')"
                :disabled="energyRequestPreviewLoading || energyRequestCount === 0"
                :loading="energyRequestPreviewLoading"
                @click="openEnergyRequestDialog"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card elevation="2">
        <v-card-title>
          <v-icon class="mr-2" color="primary">mdi-table</v-icon>
          {{ $t('billrunsDetails.resultsTitle') }}
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="detailsStore.error"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ detailsStore.error }}
          </v-alert>

          <v-alert
            v-if="detailsStore.analysisError"
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            {{ detailsStore.analysisError }}
          </v-alert>

          <v-alert
            v-if="detailsStore.dsoWarnings.length > 0"
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            {{ $t('billrunsDetails.dsoFallbackWarning') }}
          </v-alert>

          <BillrunsDetailsResultsTable
            :rows="filteredDetailRows"
            :loading="detailsStore.loading"
            :analysis-loading="detailsStore.analysisLoading"
            :has-fetched="detailsStore.hasFetched"
            :open-billrun-count="detailsStore.openBillruns.length"
            :detail-row-count="detailsStore.detailRows.length"
            :max-date-range-count="maxDateRangeCount"
            :is-data-fully-loaded="isDataFullyLoaded"
          />
        </v-card-text>
      </v-card>
    </v-container>

    <BillrunsEnergyDataRequestDialog
      v-model="energyRequestDialogOpen"
      :summary="energyRequestPreview.summary"
      :submitting="submitting"
      :submission-progress="submissionProgress"
      @confirm="confirmEnergyDataRequests"
      @cancel="energyRequestDialogOpen = false"
    />
  </v-container>
</template>

<style scoped>
.offset-days-field {
  flex: 0 0 140px;
  max-width: 140px;
}

.filter-field {
  flex: 1 1 240px;
  min-width: 240px;
  max-width: 360px;
}
</style>
